/**
 * DataDog Initialization for Claims Portal
 * Simplified version without @croixbleue/frontend-lib-ui dependency
 */

import { datadogLogs } from "@datadog/browser-logs";
import type { LogsEvent } from "@datadog/browser-logs";
import { isThirdPartyError } from "./handle-third-party-errors";
import { maskData } from "./mask-data";
import { ping } from "./ping";
import { getSessionId } from "./sessionManager";
import { getLoggingContext } from "./loggingContext";

/**
 * Filter and transform logs before sending to DataDog
 */
const beforeSend = (log: LogsEvent): boolean | void => {
    // Mask PII in log context
    if (log?.context) {
        try {
            log.context = maskData(log.context, true);
        } catch (err) {
            console.warn('[DataDog] Failed to mask log context:', err);
            return false; // Don't send if masking fails
        }
    }

    // Downgrade specific error codes to info (modify status instead of creating new log to avoid recursion)
    if (log.status === "error") {
        // HTTP errors that should be logged as info (not critical)
        if (["307", "424", "429", "440", "400", "403"].some((code) =>
            log.message.includes(code)
        )) {
            log.status = "info"; // Downgrade to info
            return true; // Send as info
        }

        // Filter out third-party service errors to reduce noise
        if (isThirdPartyError(log.message)) {
            log.status = "warn"; // Downgrade to warning
            return true; // Send as warning
        }
    }

    // Allow log to be sent
    return true;
};

/**
 * Initialize DataDog Logs
 */
export async function setupDataDog() {
    // Initialize DataDog Logs only (no RUM for now)
    if (import.meta.env.VITE_DATADOG_TOKEN) {
        const initConfig: any = {
            clientToken: import.meta.env.VITE_DATADOG_TOKEN,
            forwardErrorsToLogs: true,
            sessionSampleRate: 100, // Log 100% of sessions
            service: import.meta.env.VITE_APP_NAME || 'claims-portal-frontend-web',
            env: import.meta.env.VITE_APP_ENV || 'dev',
            version: import.meta.env.VITE_APP_VERSION || '1.0.0',
            site: import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com',
            beforeSend: beforeSend,
        };

        let isDataDogEndpointReachable = false;

        // Use custom intake URL instead of proxy to avoid ?ddforward parameter
        if (import.meta.env.VITE_DATADOG_PROXY) {
            initConfig.intakeUrl = import.meta.env.VITE_DATADOG_PROXY;
            console.info('[DataDog] Using custom intake URL:', import.meta.env.VITE_DATADOG_PROXY);
        } else {
            // Only check endpoint reachability if no proxy is configured
            const dataDogEndpointReachabilityTimeout = 3000;
            console.info('[DataDog] No proxy configured, checking direct endpoint reachability...');

            isDataDogEndpointReachable = await ping(
                `https://logs.browser-intake-datadoghq.com/api/v2/logs?dd-api-key=${import.meta.env.VITE_DATADOG_TOKEN}`,
                dataDogEndpointReachabilityTimeout,
            );

            if (!isDataDogEndpointReachable) {
                console.warn('[DataDog] Direct endpoint unreachable. Consider configuring VITE_DATADOG_PROXY.');
            }
        }

        datadogLogs.init(initConfig);
        datadogLogs.setGlobalContext({
            host: window.location.host,
            env: import.meta.env.VITE_APP_ENV || "dev",
            sessionId: getSessionId(),
        });

        // Make DD_LOGS available globally for logger.ts
        (window as any).DD_LOGS = datadogLogs;

        // Subscribe to store changes to keep business context in sync (DEV-6594)
        // This ensures auto-captured errors (forwardErrorsToLogs) include business context
        try {
            const { useClaimStore } = await import('../store/claimStore');
            useClaimStore.subscribe(() => {
                const ctx = getLoggingContext();
                datadogLogs.setGlobalContextProperty('eventNumber', ctx.eventNumber);
                datadogLogs.setGlobalContextProperty('contractNumber', ctx.contractNumber);
                datadogLogs.setGlobalContextProperty('currentScreen', ctx.currentScreen);
            });
        } catch (err) {
            console.warn('[DataDog] Failed to subscribe to claim store:', err);
        }

        console.info('[DataDog] Logs initialized', {
            service: import.meta.env.VITE_APP_NAME,
            env: import.meta.env.VITE_APP_ENV,
            endpointReachable: isDataDogEndpointReachable,
            usingProxy: !!import.meta.env.VITE_DATADOG_PROXY
        });
    } else {
        console.warn('[DataDog] Token not configured - DataDog logging disabled');
    }
}